import _ from "lodash";
import React, { useEffect, useState } from "react";

// components
import UIBaseComponent from "UIComponents/UIBaseComponent";
import UIBaseContentEditable from "UIComponents/UIBaseContentEditable";
import { ColorPicker } from "@toddle-edu/ds-web";

// design system
import { fontStyles } from "@toddle-edu/ds-theme";

// modules
import {
  updateAIDataInRedux,
  updateAssessmentInRedux,
} from "Assessments/modules/AssessmentModule";
import { updateAnalysisToolDetails } from "modules/AssessmentTool/AssessmentToolModule";
import { writeAnalysisElementInCache } from "modules/AssessmentTool/AssessmentToolGraphqlHelpers";
import { getUserType } from "modules/CommonSelectors";
import { updateSettings } from "modules/ConfigurationThunks";

// third party
import { connect } from "react-redux";
import hexToRgba from "hex-to-rgba";
import classNames from "classnames";

// utils
import {
  AI_ANALYSIS_SCALE_COLOR_PALETTE,
  AI_ANALYSIS_SCALE_MAPPING,
} from "@toddle-edu/common-utils";
import { convertHtmlToText } from "Utils/AIUtils";
import { getIsAITutorModalitiesEnabled } from "Utils/ACLStoreUtils";

// string constants
import { ANALYSIS } from "Constants/stringConstants";

// scss
import classes from "./AIAnalysisScale.scss";

const styles = {
  contentEditableStyle: {
    fontSize: "1.4rem",
    lineHeight: "2rem",
    ...fontStyles.medium,
    padding: "16px 64px 16px 16px",
  },
  contentEditableViewStyle: {
    fontSize: "1.4rem",
    lineHeight: "2rem",
    ...fontStyles.medium,
    padding: "16px",
  },
  firstColumnStyle: {
    backgroundColor: "var(--purple-950)",
  },
  editContainerStyle: {
    marginTop: "12px",
  },
};

const CriteriaColumn = props => {
  const { t, value, disabled, onChange, criteriaKey, mode } = props;

  const [localValue, setLocalValue] = useState(value);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  // set local value to update the UI immediately
  const updateInputField = value => {
    // browser adds <br> when content is empty in content editable
    if (_.trim(value) === "<br>") setLocalValue("");
    else setLocalValue(value);
  };

  const onBlurInputField = () => {
    // make sure the field is never set to empty
    if (_.isEmpty(localValue) && criteriaKey !== "description") {
      setLocalValue(value);
      return;
    }

    // call mutation
    if (!_.isEqual(localValue, value)) {
      if (criteriaKey === "label") {
        onChange(convertHtmlToText(localValue)); // removing &nbsp; from the text
      } else {
        onChange(_.trim(localValue));
      }
    }
  };

  const style =
    mode === "view"
      ? styles.contentEditableViewStyle
      : styles.contentEditableStyle;

  return (
    <UIBaseContentEditable
      value={localValue}
      placeholder={t("common:typeHere")}
      updateInputField={updateInputField}
      style={style}
      minRows={1}
      disabled={disabled}
      // do not allow html tags in label as it is being used to generate evaluation
      shouldParseInPlainText={
        getIsAITutorModalitiesEnabled() || criteriaKey === "label"
      }
      shouldTrimSpace={false}
      onBlurInputField={onBlurInputField}
    />
  );
};

class AIAnalysisScale extends UIBaseComponent {
  constructor(props) {
    super(props);

    this.debouncedUpdateAIDataInRedux = _.debounce(
      this.props.updateAIDataInRedux,
      1000
    );
  }

  writeScaleElement = ({ criterion, ...params }) => {
    const { id, type } = params;
    const { updateAnalysisToolDetails } = this.props;

    writeAnalysisElementInCache({
      id,
      type,
      data: { ...criterion, ...params },
    });

    updateAnalysisToolDetails({
      input: {
        updateAnalysisToolDetails: [{ ...params }],
      },
    });
  };

  onColorChange = ({ id, criterion, color }) => {
    const {
      updateAnalysisToolDetails,
      analysisScaleTool,
      updateSettings,
    } = this.props;

    const colorCode = _.find(AI_ANALYSIS_SCALE_MAPPING, { color }).id;

    writeAnalysisElementInCache({
      id,
      type: "CRITERIA",
      data: { ...criterion, metadata: { colorCode } },
    });

    updateAnalysisToolDetails({
      input: {
        updateAnalysisToolDetails: [
          {
            id,
            type: "CRITERIA",
            metadata: { colorCode },
          },
        ],
      },
    });

    // save user preference of colors
    const { criterias: criteria } = analysisScaleTool;

    const allCriteriaColors = _.compact(
      _.map(criteria, criterion => {
        if (criterion?.id === id) return colorCode;
        return _.get(criterion, "metadata.colorCode");
      })
    );

    if (!_.isEmpty(allCriteriaColors)) {
      updateSettings({
        settings: [
          {
            name: "AIAnalysisScaleColorPreference",
            value: JSON.stringify(allCriteriaColors),
          },
        ],
        showToastMsg: false,
      });
    }
  };

  renderScale = () => {
    const { t, analysisScaleTool, mode = "edit", isStaff } = this.props;

    const { criterias: criteria } = analysisScaleTool;

    if (_.isEmpty(criteria)) return null;

    const disabled = mode === "view";

    const columnClass = classNames(
      classes.relative,
      classes.columnWithRightBorder
    );

    return (
      <div className={classes.container} role={"button"} tabIndex={0}>
        {_.map(criteria, (criterion, index) => {
          const { id, label, description, metadata = {} } = criterion || {};
          const { colorCode } = metadata || {};

          const { color = "" } =
            _.find(AI_ANALYSIS_SCALE_MAPPING, {
              id: colorCode,
            }) || {};

          const backgroundColor = hexToRgba(color, 0.2);

          const updateCriteria = ({ key, value }) => {
            this.writeScaleElement({
              id,
              [key]: value,
              type: "CRITERIA",
              criterion,
            });

            // re-generate data for that column
            this.debouncedUpdateAIDataInRedux({
              shouldGeneratePreviewChat: true,
              updatedFields: [id],
            });
          };

          const rowClass = mode === "view" ? classes.viewRow : classes.row;

          return (
            <div key={id} className={rowClass}>
              <div className={columnClass} style={{ backgroundColor }}>
                <CriteriaColumn
                  t={t}
                  criteriaKey={"label"}
                  value={label}
                  disabled={disabled}
                  onChange={value => updateCriteria({ key: "label", value })}
                  mode={mode}
                />

                {isStaff && !disabled ? (
                  <div className={classes.colorPickerContainer}>
                    <ColorPicker
                      dsVersion="2.0"
                      selectedColor={color}
                      onColorChange={params =>
                        this.onColorChange({ id, criterion, ...params })
                      }
                      isCompact={true}
                      colorPalette={AI_ANALYSIS_SCALE_COLOR_PALETTE}
                      size={"small"}
                      shape={"square"}
                    />
                  </div>
                ) : null}
              </div>

              <div className={columnClass}>
                <CriteriaColumn
                  t={t}
                  criteriaKey={"description"}
                  value={description}
                  disabled={disabled}
                  onChange={value =>
                    updateCriteria({ key: "description", value })
                  }
                  mode={mode}
                />
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  shouldShowEmpty = () => false;

  renderEdit = () => this.renderScale();
  renderView = () => this.renderScale();
}

const mapStateToProps = (state, ownProps) => {
  const { assessmentTools } = ownProps;

  const aiData = _.get(state, "assessment.aiData", {});
  const updatedFields = _.get(aiData, "updatedFields");

  const analysisScaleTool = _.get(
    _.find(
      assessmentTools?.edges || assessmentTools,
      ({ node }) => node?.assessmentToolType === ANALYSIS
    ),
    "node.assessmentToolItem",
    {}
  );

  const userType = getUserType(state);
  const isStaff = userType === "staff";

  return {
    analysisScaleTool,
    updatedFields,
    editContainerStyle: styles.editContainerStyle,
    isStaff,
  };
};

const mapActionCreators = {
  updateAIDataInRedux,
  updateAnalysisToolDetails,
  updateAssessmentInRedux,
  updateSettings,
};

export default connect(mapStateToProps, mapActionCreators)(AIAnalysisScale);
